import { Button } from "@devhop/ui";
import {
  GripVerticalIcon,
  Loader2Icon,
  PencilIcon,
  Trash2Icon,
} from "lucide-react";
import { useCallback, useId, useRef, useState } from "react";
import { uploadApi } from "@/shared/lib/upload-api";
import { uploadFileToPresignedUrl } from "@/shared/lib/upload-to-presigned";
import { AppImage } from "@/shared/ui/AppImage";
import { PresignedImageUploadTrigger } from "@/shared/ui/PresignedImageUploadTrigger";
import type { SkuMediaItem } from "../api/client";

const KEY_PREFIX = "uploads/catalog/skus";

type SkuMediaSectionProps = {
  skuId: string;
  media: SkuMediaItem[];
  isLoading?: boolean;
  canManage?: boolean;
  onAdd: (url: string) => Promise<void>;
  onDelete: (mediaId: string) => Promise<void>;
  onReplace: (mediaId: string, url: string) => Promise<void>;
  onReorder: (mediaIds: string[]) => Promise<void>;
};

function reorderArray<T>(arr: T[], fromIndex: number, toIndex: number): T[] {
  const out = [...arr];
  const [item] = out.splice(fromIndex, 1);
  if (item == null) return arr;
  out.splice(toIndex, 0, item);
  return out;
}

function sanitizeFilename(name: string): string {
  return name.replace(/[^a-zA-Z0-9._-]/g, "_").slice(0, 100) || "image";
}

function ReplaceImageButton({
  skuId,
  disabled,
  onReplaced,
}: {
  skuId: string;
  disabled?: boolean;
  onReplaced: (key: string) => Promise<void>;
}) {
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const ext = (file.name.split(".").pop() ?? "").toLowerCase();
      const safeExt = /^(jpe?g|png|gif|webp)$/.test(ext) ? ext : "jpg";
      const baseName = file.name.includes(".")
        ? file.name.slice(0, file.name.lastIndexOf("."))
        : file.name;
      const key = `${KEY_PREFIX}/${skuId}/${Date.now()}-${sanitizeFilename(baseName)}.${safeExt}`;
      const contentType = file.type || `image/${safeExt}`;
      const { uploadUrl } = await uploadApi.getPresignUrl({ key, contentType });
      await uploadFileToPresignedUrl(uploadUrl, file, { contentType });
      await onReplaced(key);
    } finally {
      setUploading(false);
    }
  };

  return (
    <>
      <input
        ref={inputRef}
        id={inputId}
        type="file"
        accept="image/*"
        className="hidden"
        disabled={disabled || uploading}
        onChange={handleChange}
        aria-label="ເພີ່ມຮູບ"
      />
      <Button
        type="button"
        variant="secondary"
        size="icon"
        className="size-9 shadow-sm"
        disabled={disabled || uploading}
        onClick={() => inputRef.current?.click()}
        aria-label="ແກ້ໄຂຮູບ"
      >
        {uploading ? (
          <Loader2Icon className="size-4 animate-spin" aria-hidden />
        ) : (
          <PencilIcon className="size-4" aria-hidden />
        )}
      </Button>
    </>
  );
}

export function SkuMediaSection({
  skuId,
  media,
  isLoading,
  canManage,
  onAdd,
  onDelete,
  onReplace,
  onReorder,
}: SkuMediaSectionProps) {
  const [draggedId, setDraggedId] = useState<string | null>(null);
  const [dropTargetIndex, setDropTargetIndex] = useState<number | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const handleDragStart = useCallback(
    (e: React.DragEvent, id: string) => {
      if (!canManage) return;
      setDraggedId(id);
      e.dataTransfer.effectAllowed = "move";
      e.dataTransfer.setData("text/plain", id);
      e.dataTransfer.setData("application/json", JSON.stringify({ id }));
    },
    [canManage],
  );

  const handleDragOver = useCallback(
    (e: React.DragEvent, index: number) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = "move";
      if (canManage && draggedId != null) setDropTargetIndex(index);
    },
    [canManage, draggedId],
  );

  const handleDragLeave = useCallback(() => {
    setDropTargetIndex(null);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent, toIndex: number) => {
      e.preventDefault();
      setDropTargetIndex(null);
      setDraggedId(null);
      if (!canManage || draggedId == null) return;
      const fromIndex = media.findIndex((m) => m.id === draggedId);
      if (fromIndex === -1 || fromIndex === toIndex) return;
      const newOrder = reorderArray(media, fromIndex, toIndex);
      void onReorder(newOrder.map((m) => m.id));
    },
    [canManage, draggedId, media, onReorder],
  );

  const handleDragEnd = useCallback(() => {
    setDraggedId(null);
    setDropTargetIndex(null);
  }, []);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="font-semibold text-foreground text-sm">ຮູບ SKU</h3>
          <p className="text-muted-foreground text-xs">
            ຮູບແລກ = Thumbnail · ລາກຈັດລຳດັບໄດ້
          </p>
        </div>
        {canManage && (
          <PresignedImageUploadTrigger
            keyPrefix={`${KEY_PREFIX}/${skuId}`}
            onSuccess={async (key) => {
              await onAdd(key);
            }}
            onSuccessMultiple={async (keys) => {
              for (const key of keys) await onAdd(key);
            }}
            variant="button"
            label="ເພີ່ມຮູບ"
            multiple
            className="shrink-0"
          />
        )}
      </div>

      {isLoading ? (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4">
          {[1, 2, 3, 4].map((i) => (
            <div
              key={i}
              className="aspect-square animate-pulse rounded-xl bg-muted"
              aria-hidden
            />
          ))}
        </div>
      ) : media.length === 0 ? (
        <p className="rounded-xl border border-muted-foreground/30 border-dashed bg-muted/10 py-12 text-center text-muted-foreground text-sm">
          ຍັງບໍ່ມີຮູບ SKU — ກົດເພີ່ມຮູບເພື່ອອັບໂຫຼດ
        </p>
      ) : (
        <ul className="grid list-none grid-cols-2 gap-4 p-0 sm:grid-cols-3 md:grid-cols-4">
          {media.map((m, index) => (
            <li
              key={m.id}
              aria-grabbed={canManage && draggedId === m.id}
              draggable={canManage && busyId == null}
              onDragStart={(e) => handleDragStart(e, m.id)}
              onDragOver={(e) => handleDragOver(e, index)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, index)}
              onDragEnd={handleDragEnd}
              className={`group relative aspect-square overflow-hidden rounded-xl border bg-muted/20 ${
                draggedId === m.id
                  ? "opacity-60"
                  : dropTargetIndex === index
                    ? "ring-2 ring-primary ring-offset-2"
                    : "border-border/60"
              } ${canManage ? "cursor-grab active:cursor-grabbing" : ""}`}
            >
              <AppImage
                src={m.url}
                alt="ຮູບ SKU"
                className="size-full"
                fit="cover"
              />
              {index === 0 && (
                <span className="absolute top-2 left-2 rounded-md bg-primary px-2 py-0.5 font-medium text-primary-foreground text-xs">
                  Thumbnail
                </span>
              )}
              {canManage && (
                <div className="absolute inset-x-0 bottom-0 flex items-center justify-between gap-1 bg-gradient-to-t from-black/70 via-black/45 to-transparent p-2 pt-8">
                  <span
                    className="flex size-9 cursor-grab items-center justify-center rounded-md bg-white/15 text-white backdrop-blur-sm active:cursor-grabbing"
                    aria-hidden
                    title="ລາກຈັດລຳດັບ"
                  >
                    <GripVerticalIcon className="size-4" />
                  </span>
                  <div className="flex gap-1">
                    <ReplaceImageButton
                      skuId={skuId}
                      disabled={busyId != null}
                      onReplaced={async (key) => {
                        setBusyId(m.id);
                        try {
                          await onReplace(m.id, key);
                        } finally {
                          setBusyId(null);
                        }
                      }}
                    />
                    <Button
                      type="button"
                      variant="destructive"
                      size="icon"
                      className="size-9 shadow-sm"
                      disabled={busyId != null}
                      onClick={async () => {
                        setBusyId(m.id);
                        try {
                          await onDelete(m.id);
                        } finally {
                          setBusyId(null);
                        }
                      }}
                      aria-label="ລຶບຮູບ"
                    >
                      <Trash2Icon className="size-4" aria-hidden />
                    </Button>
                  </div>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
